import React, { useId, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding as faBuildingRegular, faShield as faShieldRegular } from '@fortawesome/pro-regular-svg-icons'
import { faArrowRightArrowLeft, faBrowser, faCubes, faKeySkeleton, faKeySkeletonLeftRight, faShield, faSliders, faSwatchbook, faUserCrown, faUserShield, faUsersGear } from '@fortawesome/pro-solid-svg-icons'
import { HeaderLogo } from './header-logo'
import { lmsAdminVisible } from '../../view/lms-admin/lms-admin-gate'
import { useLmsEnabled } from '../../view/lms-admin/use-lms-enabled'
import { OffcanvasClose } from '../../shared/component/offcanvas'
import { Tooltip } from '../../shared/component/floating'
import {
  IconActivityLog,
  IconAiDocument,
  IconAnalytic,
  IconAnalyticCustom,
  IconAnalyticGeneral,
  IconApp,
  IconAuditRetention,
  IconAuthSession,
  IconBug,
  IconCaretDown,
  IconCaretUp,
  IconContent,
  IconDashboard,
  IconDocs,
  IconEmail,
  IconEvent,
  IconGroup,
  IconLink,
  IconList,
  IconNotification,
  IconOrganization,
  IconConsentLog,
  IconPermission,
  IconRefusedSignIn,
  IconRefusedSignInLog,
  IconRole,
  IconSelfRegister,
  IconSettings,
  IconShield,
  IconSignInMethod,
  IconSsoConnection,
  IconSysLogin,
  IconTemplate,
  IconUsers
} from '../../shared/icon/icon'
import { useAuthContext } from '../../context/auth.context'
import { ROLE_ORG, ROLE_SYS } from '../../role/role.context'
import { useRoleOrg, useRoleOrgOrganization, useRoleOrgSSO } from '../../role/hook/role-org'
import { useRoleSys } from '../../role/hook/role-sys'
import { useDocAuthRedirect } from '../../layout/unauthorized/auth/hooks/doc'
import { useStaffTools } from './staff-tools'
import { useViewerLaunch } from '../../shared/hooks/use-viewer-launch'
import { useViewerAvailable } from '../../shared/hooks/use-viewer-available'
import { ViewerLaunchFailure } from '../../shared/viewer/viewer-launch-failure'
import { canSwitchOrganization } from '../../shared/organization-switch'
import { useApiAccess } from '../../layout/authorized/org/api-keys/use-api-access'
import './sidebar-left.scss'

// The admin's left sidebar, "option D" (owner, 2026-10-07): the current
// organization's card (a switch when the person can switch) beside the
// Staff tools shield; the organization's menu in groups; and, when Staff
// tools is on, the EducationXR staff section on a darker band.
//
// Every entry keeps exactly the gate it had before the restructure: the
// gate components it used to sit in (DisplayAuth, RoleOrg*, RoleSys*,
// LoginSysAdmin, LoginSysRoot, DisplayOrgCompany, FeatureTradeShow) are
// replaced by the same tests on the same state, one `show` per entry, so a
// group can tell whether it has anything to show. sidebar-left-gates.test.js
// holds the table, recorded from the old sidebar.

// Font Awesome Pro glyphs this menu needs that shared/icon has no name for,
// drawn like the shared ones.
const faIcon = icon => ({ className, size }) => <span className={className}><FontAwesomeIcon className="mx-1" icon={icon} size={size} /></span>
const IconAssetLibrary = faIcon(faCubes)
const IconSystemRoles = faIcon(faUserShield)
const IconOrganizationRoles = faIcon(faUsersGear)
const IconOrganizationRolePermissions = faIcon(faKeySkeleton)
const IconSystemAdmins = faIcon(faUserCrown)
const IconAnalyticsSetup = faIcon(faSliders)
// The LMS presets are an Internal tools entry now, where no two entries share
// a glyph (sidebar-left-audit.test.js); IconProvider's key is System role
// permissions'.
const IconLmsPresets = faIcon(faSwatchbook)
const IconWebViewer = faIcon(faBrowser)
// Every organization's API keys: a key of its own among the staff entries
// (the role permissions have the plain and skeleton keys).
const IconApiKeyOversight = faIcon(faKeySkeletonLeftRight)

// bg-body: white in light mode, the site ground in dark mode.
const Brand = () => {
  return (
    <div className="navbar bg-body">
      <div className="container-fluid">
        <div className="navbar-brand w-100">
          <div className="d-flex justify-content-around">
            <HeaderLogo />
          </div>
        </div>
      </div>
    </div>
  )
}

// The current organization. When the session can switch to another (the
// one decision the account menu's Change Organization shares,
// shared/organization-switch) it is the way to switch: a link to the picker
// page (/auth/account), not a dropdown, so it carries a switch icon rather
// than a caret. A long name wraps to two lines, then truncates; the title
// has it whole.
const OrganizationCard = ({ name, canSwitch, target }) => {
  const body = (
    <React.Fragment>
      <FontAwesomeIcon icon={faBuildingRegular} className="edxr-sb-org__icon" />
      <span className="edxr-sb-org__text">
        <span className="edxr-sb-org__name">{name}</span>
        <span className="edxr-sb-org__sub">Organization</span>
      </span>
      {canSwitch && <FontAwesomeIcon icon={faArrowRightArrowLeft} className="edxr-sb-org__switch" aria-hidden="true" />}
    </React.Fragment>
  )

  if (!canSwitch) {
    return <div className="edxr-sb-org" title={name} data-testid="sidebar-org-card">{body}</div>
  }

  return (
    <div className="edxr-sb-org-slot">
      <OffcanvasClose target={target}>
        <Link
          className="edxr-sb-org edxr-sb-org--switch"
          to="/auth/account"
          title={`${name || 'Organization'}: switch organization`}
          aria-label={`${name || 'Organization'}, Organization. Switch organization`}
          data-testid="sidebar-org-card">
          {body}
        </Link>
      </OffcanvasClose>
    </div>
  )
}

// Shows and hides the staff section. Staff only, never while impersonating.
const StaffToolsButton = ({ on, onToggle }) => {
  const label = on ? 'Hide internal tools' : 'Show internal tools'

  return (
    <Tooltip label={<span className="edxr-sb-tip">{label}</span>} placement="bottom">
      <button type="button" className={`edxr-sb-shield${on ? ' is-on' : ''}`} aria-label="Internal tools" aria-pressed={on} onClick={onToggle}>
        <FontAwesomeIcon icon={on ? faShield : faShieldRegular} />
      </button>
    </Tooltip>
  )
}

// The entries look as they did before the rebuild (owner feedback
// 2026-10-07), with the new sentence-case labels: Bootstrap's nav-pills
// nav-link inside a padded nav-item -- 1rem light text, the large icons in a
// 40 px column, and the grey rounded pill inset from both edges on hover
// (the theme's .edxr-sidebar-left-item) and on the current page. Never
// underlined: the admin's global rule (src/scss/links.scss) covers every
// link and button, so the sidebar carries no opt-out of its own.
const ITEM_LI = 'nav-item p-1 fs-6'
const ITEM = 'edxr-sb-item nav-link d-flex align-items-center w-100 text-start fw-light edxr-sidebar-left-item edxr-pointer'
const LIST = 'nav nav-pills flex-column'

// The current page: the entry's own path, or a page under it.
const isSelected = (pathname, to) => !!to && (pathname === to || String(pathname || '').startsWith(`${to}/`))

// An entry that opens a section's first page names the section (`section`)
// when its other pages are the same entry: LMS opens /org/analytic/lms/launches
// and stays selected on every page under /org/analytic/lms.
const pathOf = item => item.section || item.to

const ItemBody = ({ Icon, title, children }) =>
  <React.Fragment>
    <span className="edxr-sb-item__icon text-center" style={{ width: 40 }} aria-hidden="true">{Icon && <Icon size="lg" />}</span>
    <span className="edxr-sb-item__label mx-2 flex-fill">{title}</span>
    {children}
  </React.Fragment>

const ItemLink = ({ Icon, title, to, section, target, pathname }) =>
  <li className={ITEM_LI}>
    <OffcanvasClose target={target}>
      <Link className={ITEM} to={to} aria-current={isSelected(pathname, section || to) ? 'page' : undefined}>
        <ItemBody Icon={Icon} title={title} />
      </Link>
    </OffcanvasClose>
  </li>

const ItemButton = ({ Icon, title, onClick, disabled, target }) =>
  <li className={ITEM_LI}>
    <OffcanvasClose target={target}>
      <button type="button" className={ITEM} onClick={onClick} disabled={disabled}>
        <ItemBody Icon={Icon} title={title} />
      </button>
    </OffcanvasClose>
  </li>

// An entry with pages under it (Analytics: General, Custom). The pages stay
// in the document, collapsed, indented under it as before.
const ItemCollapse = ({ Icon, title, items, target, pathname, startOpen }) => {
  const id = useId()
  const [open, updateOpen] = useState(!!startOpen)

  return (
    <li className={ITEM_LI}>
      <button type="button" className={ITEM} aria-expanded={open} aria-controls={id} onClick={() => updateOpen(!open)}>
        <ItemBody Icon={Icon} title={title}>
          <span className="edxr-sb-item__state" aria-hidden="true">{open ? <IconCaretUp size="lg" /> : <IconCaretDown size="lg" />}</span>
        </ItemBody>
      </button>
      <ul id={id} className={`edxr-sb-sub ${LIST} ms-3 collapse${open ? ' show' : ''}`}>
        {items.map(item => <ItemLink key={item.to} {...item} target={target} pathname={pathname} />)}
      </ul>
    </li>
  )
}

const Item = ({ item, target, pathname }) => {
  if (item.items) return <ItemCollapse {...item} target={target} pathname={pathname} startOpen={item.items.some(sub => pathname.startsWith(pathOf(sub)))} />
  if (item.onClick) return <ItemButton {...item} target={target} />
  return <ItemLink {...item} target={target} pathname={pathname} />
}

// A group: its heading only above at least one entry it shows.
const Group = ({ title, tag, items, testId, target, pathname }) => {
  const id = useId()
  const shown = items.filter(item => item.show)

  if (!shown.length) return null

  return (
    <div className="edxr-sb-group" role="group" aria-labelledby={id}>
      <div className="edxr-sb-heading" id={id}>
        <span data-testid={testId}>{title}</span>
        {tag && <span className="edxr-sb-tag">{tag}</span>}
      </div>
      <ul className={`edxr-sb-list ${LIST}`}>
        {shown.map(item => <Item key={item.title} item={item} target={target} pathname={pathname} />)}
      </ul>
    </div>
  )
}

/// /////////////////////////////////////////////////

export const SidebarLeft = ({ target }) => {
  const authContext = useAuthContext()
  const { pathname } = useLocation()
  const docAuthRedirect = useDocAuthRedirect()
  const titleId = useId()

  const handleDocs = async () => docAuthRedirect()

  const state = authContext.state
  const claims = state.jwtClaims || {}

  // The gates, as the components they replace computed them.
  const isAuth = !!state.accessToken // DisplayAuth
  const staff = !!(state.isSysRoot || state.isSysAdmin) // LoginSysAdmin
  const root = !!state.isSysRoot // LoginSysRoot
  const company = claims.organizationType === 'company' // DisplayOrgCompany
  const tradeShow = !!claims.featureTradeShow // FeatureTradeShow
  const impersonating = !!claims.impersonationEndsAt

  // RoleOrg* (a super admin or root reads every organization scope)
  const dashboard = useRoleOrg(ROLE_ORG.DASHBOARD)
  const analytic = useRoleOrg(ROLE_ORG.ANALYTIC)
  const node = useRoleOrg(ROLE_ORG.NODE)
  const user = useRoleOrg(ROLE_ORG.USER)
  const group = useRoleOrg(ROLE_ORG.GROUP)
  const tradeshow = useRoleOrg(ROLE_ORG.TRADESHOW)
  const doc = useRoleOrg(ROLE_ORG.DOC)
  const auditLog = useRoleOrg(ROLE_ORG.AUDIT_LOG)
  const lms = useRoleOrg(ROLE_ORG.LMS)
  const apiKey = useRoleOrg(ROLE_ORG.API_KEY)
  // the Settings rules, shared with the account menu
  const organization = useRoleOrgOrganization()
  const sso = useRoleOrgSSO()

  // RoleSys* (root reads every system scope)
  const sysApp = useRoleSys(ROLE_SYS.SYS_APP)
  const sysOrganization = useRoleSys(ROLE_SYS.SYS_ORGANIZATION)
  const sysLogin = useRoleSys(ROLE_SYS.SYS_LOGIN)
  const sysAuthStatus = useRoleSys(ROLE_SYS.SYS_AUTH_STATUS)
  const sysProvider = useRoleSys(ROLE_SYS.SYS_PROVIDER)
  const sysErrors = useRoleSys(ROLE_SYS.SYS_ERRORS)
  const sysRequestLog = useRoleSys(ROLE_SYS.SYS_REQUEST_LOG)
  const sysAuditLog = useRoleSys(ROLE_SYS.SYS_AUDIT_LOG)
  const sysAuditRetention = useRoleSys(ROLE_SYS.SYS_AUDIT_RETENTION)
  const sysAlertRouting = useRoleSys(ROLE_SYS.SYS_ALERT_ROUTING)
  const sysSendgridEvent = useRoleSys(ROLE_SYS.SYS_SENDGRID_EVENT)
  const sysLmsPreset = useRoleSys(ROLE_SYS.SYS_LMS_PRESET)
  const sysApiKey = useRoleSys(ROLE_SYS.SYS_API_KEY)
  const sysNotify = useRoleSys(ROLE_SYS.SYS_NOTIFY)
  const sysAiToolsTelemetry = useRoleSys(ROLE_SYS.SYS_AI_TOOLS_TELEMETRY)
  const sysConsentLog = useRoleSys(ROLE_SYS.SYS_CONSENT_LOG)
  const notify = Boolean(process.env.REACT_APP_API_NOTIFY) // the build knows the notify service (api-new/state/notify notifyAvailable)

  // Staff tools: the shield is for super admins and roots, never in an
  // impersonated session. A root impersonating a super admin holds a super
  // admin's token, whose staff entries the sidebar has always listed; with
  // no shield to open them, they are simply shown.
  const staffTools = useStaffTools({ allowed: staff && !impersonating, pathname })

  // Internal tools open at the top of the menu (owner 2026-10-07): turning
  // them on also scrolls the menu to the top, so what was just switched on
  // is on screen.
  const navRef = useRef(null)
  const toggleStaffTools = () => {
    const turningOn = !staffTools.on
    staffTools.toggle()
    if (turningOn && navRef.current) navRef.current.scrollTop = 0
  }
  const showStaff = staff && (impersonating || staffTools.on)

  // API keys (Phase D, Task 15): API_KEY read, and the organization's
  // partner API access on. Like the LMS, an organization without the feature
  // never sees the entry, staff included (owner 2026-11-02, replacing the
  // 2026-10-23 rule that showed it whatever the access): staff switch the
  // access on from the organization's page (Internal tools > Organizations),
  // and an organization's keys stay in Internal tools > API keys. A pending
  // or unreadable answer hides it, and only a session with API_KEY read asks
  // (use-api-access.js: once per session and organization).
  const apiKeysCouldShow = isAuth && !!apiKey.accessRead
  const apiAccess = useApiAccess(apiKeysCouldShow)
  const apiKeysAllowed = apiKeysCouldShow && !!apiAccess && apiAccess.enabled === true

  // The LMS entries (Analytics > LMS, Integrations > LMS) also need the
  // current organization to have the LMS feature. Staff hold every
  // organization permission, so the roles never say no in an organization
  // without it; the sign-in token has no such claim, so the LMS service is
  // asked (use-lms-enabled.js: once per organization; FEATURE_DISABLED hides,
  // a pending answer hides, any other failure shows). Only a session that
  // could see an LMS entry at all asks: nobody else's session calls the LMS.
  const lmsCouldShow = isAuth && lmsAdminVisible(state) && (!!lms.accessRead || !!analytic.accessRead)
  const lmsEnabled = useLmsEnabled(lmsCouldShow)

  // Web viewer: the web viewer's home, in a new tab, for this session (a
  // launch code with no target, shared/hooks/use-viewer-launch.js, the code
  // path of a course's "Launch"). Open to every signed-in person since the
  // viewer opened to everyone (owner 2026-10-08; it was a super-admin staff
  // tool), an impersonated session included (it stays the impersonation).
  // Called straight from the click: the tab opens before any await. Shown
  // only where this environment has a viewer (auth's GET /launch/available,
  // read once per session): a launch there can only fail.
  const webViewerAllowed = isAuth
  const viewerAvailable = useViewerAvailable(webViewerAllowed)
  const viewerLaunch = useViewerLaunch()
  const handleWebViewer = () => {
    viewerLaunch.launch()
  }

  const orgGroups = [
    {
      title: 'Overview',
      items: [
        { title: 'Dashboard', Icon: IconDashboard, to: '/org/dashboard', show: isAuth && !!dashboard.accessRead },
        { title: 'Web viewer', Icon: IconWebViewer, onClick: handleWebViewer, disabled: viewerLaunch.launching, show: webViewerAllowed && viewerAvailable },
        {
          title: 'Analytics',
          Icon: IconAnalytic,
          show: isAuth && !!analytic.accessRead,
          items: [
            { title: 'General', Icon: IconAnalyticGeneral, to: '/org/analytic/general' },
            { title: 'Custom', Icon: IconAnalyticCustom, to: '/org/analytic/custom' },
            // The LMS insights pages: whoever reads Analytics and sees the LMS
            // admin (system administrators and root while the staff-only
            // switch is closed, staff-only.js).
            ...(lmsAdminVisible(state) && lmsEnabled ? [{ title: 'LMS', Icon: IconLink, to: '/org/analytic/lms/launches', section: '/org/analytic/lms' }] : [])
          ]
        }
      ]
    },
    {
      title: 'Library',
      items: [
        { title: 'Content', Icon: IconContent, to: '/org/node-content', show: isAuth && !!node.accessRead },
        { title: 'Templates', Icon: IconTemplate, to: '/org/node-template', show: isAuth && !!node.accessRead },
        { title: 'Asset library', Icon: IconAssetLibrary, to: '/org/node-library', show: isAuth && !!node.accessRead && staff }
      ]
    },
    {
      title: 'People',
      items: [
        { title: 'Users', Icon: IconUsers, to: '/org/user/list', show: isAuth && !!user.accessRead },
        { title: 'Groups', Icon: IconGroup, to: '/org/group/list', show: isAuth && !!group.accessRead },
        { title: 'Self register', Icon: IconSelfRegister, to: '/org/self-register/list', show: isAuth && !!tradeshow.accessRead && tradeShow }
      ]
    },
    {
      title: 'Activity',
      items: [
        { title: 'Audit log', Icon: IconActivityLog, to: '/org/audit-log', show: isAuth && !!auditLog.accessRead },
        { title: 'Email activity', Icon: IconEmail, to: '/org/email-activity', show: isAuth && !!user.accessUpdate },
        { title: 'Notification activity', Icon: IconNotification, to: '/org/notification-activity', show: isAuth && !!user.accessUpdate && notify }
      ]
    },
    {
      // The LMS connector (feat/lms-connector). While the staff-only switch is
      // closed (C1, view/lms-admin/staff-only.js) the whole entry is a system
      // administrator's or root's alone: absent for anyone else, not
      // disabled. It also needs the current organization to have the LMS
      // (lmsEnabled above). The LMS has no API keys entry of its own (Phase D
      // D7): its keys are core API keys, the organization's on API keys
      // above, a connection's in its drawer. The platform's LMS presets are
      // not here (owner 2026-10-20): they are an Internal tools entry in
      // Platform.
      title: 'Integrations',
      items: [
        // Whoever reads API keys, while the organization's API access is on
        // (apiKeysAllowed above).
        { title: 'API keys', Icon: IconPermission, to: '/org/api-keys', show: apiKeysAllowed },
        {
          title: 'LMS',
          Icon: IconLink,
          show: isAuth && !!lms.accessRead && lmsAdminVisible(state) && lmsEnabled,
          items: [
            { title: 'Connections', Icon: IconLink, to: '/org/lms/connections' },
            { title: 'Courses', Icon: IconContent, to: '/org/lms/courses' },
            { title: 'Account linking', Icon: IconUsers, to: '/org/lms/account-links' },
            { title: 'Sessions', Icon: IconApp, to: '/org/lms/sessions' },
            { title: 'Activity', Icon: IconEvent, to: '/org/lms/activity' },
            { title: 'Plan & usage', Icon: IconAnalytic, to: '/org/lms/usage' },
            { title: 'Settings', Icon: IconSettings, to: '/org/lms/settings' }
          ]
        }
      ]
    },
    {
      title: 'Settings',
      items: [
        { title: 'Organization', Icon: IconOrganization, to: '/org/organization', show: isAuth && !!organization.accessRead },
        { title: 'Authentication', Icon: IconShield, to: '/org/sso', show: isAuth && !!sso.accessRead },
        { title: 'Docs', Icon: IconDocs, onClick: handleDocs, show: isAuth && company && !!doc.accessRead }
      ]
    }
  ]

  const staffGroups = [
    {
      title: 'Organizations & people',
      items: [
        { title: 'Organizations', Icon: IconOrganization, to: '/admin/organization/list', show: staff && !!sysOrganization.accessRead },
        { title: 'Logins', Icon: IconSysLogin, to: '/admin/login/list', show: staff && !!sysLogin.accessRead },
        { title: 'Auth sessions', Icon: IconAuthSession, to: '/admin/auth-session/list', show: staff && !!sysAuthStatus.accessRead }
      ]
    },
    {
      title: 'Sign-in & SSO',
      items: [
        { title: 'SSO connections', Icon: IconSsoConnection, to: '/admin/provider/list', show: staff && !!sysProvider.accessRead },
        { title: 'Sign-in methods', Icon: IconSignInMethod, to: '/admin/signin-method', show: staff && !!sysProvider.accessRead },
        { title: 'Refused sign-ins', Icon: IconRefusedSignIn, to: '/admin/device-login-denial/summary', show: staff && !!sysAuthStatus.accessRead },
        { title: 'Refused sign-in log', Icon: IconRefusedSignInLog, to: '/admin/device-login-denial/list', show: staff && !!sysAuthStatus.accessRead }
      ]
    },
    {
      title: 'Monitoring',
      items: [
        { title: 'Errors', Icon: IconBug, to: '/admin/errors', show: staff && !!sysErrors.accessRead },
        { title: 'Request log', Icon: IconList, to: '/admin/request-log', show: staff && !!sysRequestLog.accessRead },
        { title: 'Audit log', Icon: IconActivityLog, to: '/admin/audit-log', show: staff && !!sysAuditLog.accessRead },
        { title: 'Audit retention', Icon: IconAuditRetention, to: '/admin/audit-retention', show: staff && !!sysAuditRetention.accessRead }
      ]
    },
    {
      title: 'Alerts & email',
      items: [
        { title: 'Alert routing', Icon: IconNotification, to: '/admin/alert-routing', show: staff && !!sysAlertRouting.accessRead },
        { title: 'Email activity', Icon: IconEmail, to: '/admin/email-activity', show: staff && !!sysSendgridEvent.accessRead },
        { title: 'Notifications', Icon: IconNotification, to: '/admin/notify', show: staff && !!sysNotify.accessRead && notify }
      ]
    },
    {
      // Apps (SYS_APP read) is not root-only, so it sits apart from the Root
      // group (owner 2026-10-07).
      title: 'Platform',
      items: [
        { title: 'Apps', Icon: IconApp, to: '/admin/app', show: staff && !!sysApp.accessRead },
        // Every organization's API keys and their usage (Phase D, Task 16).
        { title: 'API keys', Icon: IconApiKeyOversight, to: '/admin/api-keys', show: staff && !!sysApiKey.accessRead },
        // AI tools telemetry (spec 2026-10-20; contract §8, R32): sessions,
        // postmortems and findings with their processing marks, beside the
        // API keys whose telemetry key exports them.
        { title: 'AI tools telemetry', Icon: IconAiDocument, to: '/admin/ai-tools', show: staff && !!sysAiToolsTelemetry.accessRead },
        // The website's consent log: look up a consent ID, verify a signed
        // one, export a date range as CSV.
        { title: 'Consent log', Icon: IconConsentLog, to: '/admin/consent-log', show: staff && !!sysConsentLog.accessRead },
        // The LMS connector's platform templates (owner 2026-10-20: they were
        // under Integrations > LMS). A system administrator holding the
        // SYS_LMS_PRESET scope (root reads every system scope), as the
        // server's preset routes need; platform-wide, so it never depends on
        // the current organization's LMS.
        { title: 'LMS presets', Icon: IconLmsPresets, to: '/org/lms/presets', show: staff && !!sysLmsPreset.accessRead }
      ]
    },
    {
      // Root only: every entry needs root, so the tag is always true.
      title: 'Root',
      tag: 'ROOT',
      items: [
        { title: 'System roles', Icon: IconSystemRoles, to: '/root/role-sys/list', show: root },
        { title: 'System role permissions', Icon: IconRole, to: '/root/role-sys-permission/list', show: root },
        { title: 'Organization roles', Icon: IconOrganizationRoles, to: '/root/role-org/list', show: root },
        { title: 'Organization role permissions', Icon: IconOrganizationRolePermissions, to: '/root/role-org-permission/list', show: root },
        { title: 'System admins', Icon: IconSystemAdmins, to: '/root/sys-admin/list', show: root },
        { title: 'Analytics setup', Icon: IconAnalyticsSetup, to: '/root/analytic-configure', show: root }
      ]
    }
  ]

  const anyStaffEntry = staffGroups.some(g => g.items.some(item => item.show))

  return (
    <div className="edxr-sb d-flex flex-column bg-primary h-100">
      <div>
        <div className="border-bottom border-secondary" style={{ height: '58px' }}>
          <Brand />
        </div>
      </div>

      {(isAuth || staff) &&
        <div className="edxr-sb-top">
          {isAuth && <OrganizationCard name={state.organizationName || claims.organizationName} canSwitch={canSwitchOrganization(claims)} target={target} />}
          {staff && !impersonating && <StaffToolsButton on={staffTools.on} onToggle={toggleStaffTools} />}
        </div>}

      <nav className="edxr-sb-scroll flex-fill" aria-label="Admin menu" ref={navRef}>
        {showStaff && anyStaffEntry &&
          <section className="edxr-sb-staff" aria-labelledby={titleId}>
            <div className="edxr-sb-staff__head">
              <FontAwesomeIcon icon={faShieldRegular} className="edxr-sb-staff__shield" />
              <span className="edxr-sb-staff__text">
                <span className="edxr-sb-staff__title" id={titleId}>Internal tools</span>
              </span>
            </div>
            {staffGroups.map(g => <Group key={g.title} {...g} testId="sidebar-group-heading" target={target} pathname={pathname} />)}
          </section>}

        <div className="edxr-sb-org-menu">
          {orgGroups.map(g => <Group key={g.title} {...g} testId="sidebar-org-group-heading" target={target} pathname={pathname} />)}
        </div>
      </nav>

      {/* Web viewer's failure to open, in the shared launch-failure modal: the
          sidebar sits outside the page's NotificationProvider. */}
      {viewerLaunch.failure && <ViewerLaunchFailure failure={viewerLaunch.failure} onClose={viewerLaunch.dismiss} />}
    </div>
  )
}
